/**
 * Metadata Filters Hook - Ported from PAAB app
 *
 * Manages state and filter construction for enhanced metadata filters.
 * Uses a pending/applied pattern where user edits are "pending" until
 * they click Apply to commit them.
 */

import { useCallback, useEffect, useMemo, useReducer } from "react";
import {
  APS_CATEGORY_MAPPING,
  APS_SUBCATEGORY_MAPPING,
  getAllDiscreteValues,
} from "../config/filter-mappings";
import { ComparisonFilter, CompoundFilter } from "../types/assistant";

// ========== Filter Types ==========

export type MetadataFilter = ComparisonFilter | CompoundFilter | null;

// ========== Metadata Values ==========

export const AUDIENCE_VALUES = ["HCP", "Patient", "Consumer"] as const;
export const EXTERNAL_REVIEW_VALUES = ["PAAB", "ASC", "Exempt"] as const;
export const PROMOTION_TYPE_VALUES = [
  "Branded (product specific)",
  "Unbranded (disease awareness or educational)",
  "Unbranded (corporate assets)",
] as const;
export const NOC_STATUS_VALUES = ["pre-NOC", "NOC", "NOC-C"] as const;

export const TOPIC_VALUES = [
  "Efficacy (treatment outcomes)",
  "Safety / monitoring / warnings",
  "Dosing / administration",
  "Mechanism of action",
  "Product information",
  "Disease information",
  "Patient support programs",
  "Healthcare professional resources",
  "Clinical trial information",
  "Comparative information",
  "Epidemiology / burden of disease",
  "Pharmacoeconomics / cost-effectiveness",
  "Quality of life",
  "Treatment guidelines",
  "Formulary / reimbursement",
  "Indication / approved use",
  "Contraindications / precautions",
  "Drug interactions",
  "Adverse reactions",
  "Storage / handling",
  "Pharmacokinetics / pharmacodynamics",
  "Population-specific information",
  "Off-label use",
  "Brand / corporate messaging",
  "Unbranded disease education",
  "Professional education",
  "Patient education",
] as const;

// Dynamic year bounds for date filters
const currentYear = new Date().getFullYear();
export const MIN_YEAR = 2000;
export const MAX_YEAR = currentYear + 5;

// ========== Literal Types from Constants ==========

export type AudienceValue = (typeof AUDIENCE_VALUES)[number];
export type ExternalReviewValue = (typeof EXTERNAL_REVIEW_VALUES)[number];
export type PromotionTypeValue = (typeof PROMOTION_TYPE_VALUES)[number];
export type NocStatusValue = (typeof NOC_STATUS_VALUES)[number];
export type TopicValue = (typeof TOPIC_VALUES)[number];

// ========== State Types ==========

export type FiltersState = {
  clientIds: string[];
  apsCategory: string[];
  apsSubcategory: string[];
  audience: AudienceValue | null;
  externalReview: ExternalReviewValue | null;
  promotionType: PromotionTypeValue | null;
  nocStatus: NocStatusValue | null;
  topic: TopicValue[];
  startYear: number | null;
  approvalYear: number | null;
};

// Combined state with pending and applied filters
type CombinedState = {
  pending: FiltersState;
  applied: FiltersState;
};

type FiltersAction =
  | { type: "setClientIds"; values: string[] }
  | { type: "setApsCategory"; values: string[] }
  | { type: "setApsSubcategory"; values: string[] }
  | { type: "setAudience"; value: AudienceValue | null }
  | { type: "setExternalReview"; value: ExternalReviewValue | null }
  | { type: "setPromotionType"; value: PromotionTypeValue | null }
  | { type: "setNocStatus"; value: NocStatusValue | null }
  | { type: "setTopic"; values: TopicValue[] }
  | { type: "setStartYear"; value: number | null }
  | { type: "setApprovalYear"; value: number | null }
  | { type: "apply" }
  | { type: "reset" };

const initialFilters: FiltersState = {
  clientIds: [],
  apsCategory: [],
  apsSubcategory: [],
  audience: null,
  externalReview: null,
  promotionType: null,
  nocStatus: null,
  topic: [],
  startYear: null,
  approvalYear: null,
};

// ========== localStorage Persistence ==========

const STORAGE_KEY = "home-metadata-filters";

function saveFiltersToStorage(state: CombinedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save filters to localStorage:", e);
  }
}

function loadFiltersFromStorage(): CombinedState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    // Handle legacy format (just FiltersState) vs new format (CombinedState)
    if (isValidFiltersState(parsed)) {
      // Migrate legacy format
      return { pending: parsed, applied: parsed };
    }
    if (isValidCombinedState(parsed)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function isValidFiltersState(obj: unknown): obj is FiltersState {
  if (!obj || typeof obj !== "object") return false;
  const s = obj as Record<string, unknown>;
  return (
    Array.isArray(s.clientIds) &&
    Array.isArray(s.apsCategory) &&
    Array.isArray(s.apsSubcategory) &&
    (s.audience === null || typeof s.audience === "string") &&
    (s.externalReview === null || typeof s.externalReview === "string") &&
    (s.promotionType === null || typeof s.promotionType === "string") &&
    (s.nocStatus === null || typeof s.nocStatus === "string") &&
    Array.isArray(s.topic) &&
    (s.startYear === null || typeof s.startYear === "number") &&
    (s.approvalYear === null || typeof s.approvalYear === "number")
  );
}

function isValidCombinedState(obj: unknown): obj is CombinedState {
  if (!obj || typeof obj !== "object") return false;
  const s = obj as Record<string, unknown>;
  return isValidFiltersState(s.pending) && isValidFiltersState(s.applied);
}

function clearFiltersStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }
}

const getInitialState = (): CombinedState => {
  const stored = loadFiltersFromStorage();
  if (stored) return stored;
  return { pending: initialFilters, applied: initialFilters };
};

// ========== Reducer ==========

const filterReducer = (
  state: CombinedState,
  action: FiltersAction
): CombinedState => {
  switch (action.type) {
    // All set* actions update pending only
    case "setClientIds":
      return { ...state, pending: { ...state.pending, clientIds: action.values } };
    case "setApsCategory":
      return { ...state, pending: { ...state.pending, apsCategory: action.values } };
    case "setApsSubcategory":
      return { ...state, pending: { ...state.pending, apsSubcategory: action.values } };
    case "setAudience":
      return { ...state, pending: { ...state.pending, audience: action.value } };
    case "setExternalReview":
      return { ...state, pending: { ...state.pending, externalReview: action.value } };
    case "setPromotionType":
      return { ...state, pending: { ...state.pending, promotionType: action.value } };
    case "setNocStatus":
      return { ...state, pending: { ...state.pending, nocStatus: action.value } };
    case "setTopic":
      return { ...state, pending: { ...state.pending, topic: action.values } };
    case "setStartYear":
      return { ...state, pending: { ...state.pending, startYear: action.value } };
    case "setApprovalYear":
      return { ...state, pending: { ...state.pending, approvalYear: action.value } };
    // Apply copies pending -> applied
    case "apply":
      return { ...state, applied: { ...state.pending } };
    // Reset clears both
    case "reset":
      clearFiltersStorage();
      return { pending: initialFilters, applied: initialFilters };
    default:
      return state;
  }
};

// ========== Filter Builder Helpers ==========

/**
 * Build topic filter (OR on both primary and secondary topic fields)
 * Supports multiple topics - matches if any selected topic appears in primary OR secondary
 */
function buildTopicFilter(topics: string[]): CompoundFilter {
  // For each topic, check if it appears in primary_topic OR secondary_topic
  // Then OR all the topic conditions together
  const topicFilters: CompoundFilter[] = topics.map((topic) => ({
    type: "or",
    filters: [
      { type: "eq", key: "primary_topic", value: topic },
      { type: "eq", key: "secondary_topic", value: topic },
    ],
  }));

  return {
    type: "or",
    filters: topicFilters,
  };
}

/**
 * Build category filter with Internal exclusion
 */
function buildCategoryFilter(discreteValues: string[]): CompoundFilter {
  return {
    type: "and",
    filters: [
      { type: "ne", key: "aps_category", value: "Internal" },
      { type: "in", key: "aps_category", value: discreteValues },
    ],
  };
}

/**
 * Build file search filters from a FiltersState
 * @param filters - The current filter state
 * @param excludedClientIds - Client IDs that the user CANNOT access (always excluded)
 */
function buildFileSearchFilters(
  filters: FiltersState,
  excludedClientIds: string[] = []
): MetadataFilter {
  const filterList: (ComparisonFilter | CompoundFilter)[] = [];

  // ALWAYS exclude restricted clients (ethical wall - defense in depth)
  // This runs even if user hasn't selected any client filter
  if (excludedClientIds.length > 0) {
    filterList.push({
      type: "nin",
      key: "clientId",
      value: excludedClientIds,
    });
  }

  // User-selected client inclusion filter (if any)
  if (filters.clientIds.length > 0) {
    filterList.push({ type: "in", key: "clientId", value: filters.clientIds });
  }

  // APS Category (map abstracted -> discrete values, with Internal exclusion)
  if (filters.apsCategory.length > 0) {
    const discreteValues = getAllDiscreteValues(
      filters.apsCategory,
      APS_CATEGORY_MAPPING
    );
    if (discreteValues.length > 0) {
      filterList.push(buildCategoryFilter(discreteValues));
    }
  }

  // APS Sub-category (map abstracted -> discrete values)
  if (filters.apsSubcategory.length > 0) {
    const discreteValues = getAllDiscreteValues(
      filters.apsSubcategory,
      APS_SUBCATEGORY_MAPPING
    );
    if (discreteValues.length > 0) {
      filterList.push({
        type: "in",
        key: "aps_subcategory",
        value: discreteValues,
      });
    }
  }

  // Audience
  if (filters.audience) {
    filterList.push({ type: "eq", key: "audience", value: filters.audience });
  }

  // External Review
  if (filters.externalReview) {
    filterList.push({
      type: "eq",
      key: "external_review",
      value: filters.externalReview,
    });
  }

  // Promotion Type
  if (filters.promotionType) {
    filterList.push({
      type: "eq",
      key: "promotion_type",
      value: filters.promotionType,
    });
  }

  // NOC Status
  if (filters.nocStatus) {
    filterList.push({
      type: "eq",
      key: "noc_status",
      value: filters.nocStatus,
    });
  }

  // Topic (search both primary_topic and secondary_topic)
  if (filters.topic.length > 0) {
    filterList.push(buildTopicFilter(filters.topic));
  }

  // Start Year - filter for start_date >= Jan 1 of selected year (Unix timestamp)
  if (filters.startYear) {
    const startOfYear = new Date(filters.startYear, 0, 1, 0, 0, 0).getTime() / 1000;
    filterList.push({
      type: "gte",
      key: "start_date",
      value: startOfYear,
    });
  }

  // Approval Year - filter for approval_date >= Jan 1 of selected year (Unix timestamp)
  if (filters.approvalYear) {
    const startOfYear = new Date(filters.approvalYear, 0, 1, 0, 0, 0).getTime() / 1000;
    filterList.push({
      type: "gte",
      key: "approval_date",
      value: startOfYear,
    });
  }

  // Combine all filters with AND
  if (filterList.length === 0) return null;
  if (filterList.length === 1) return filterList[0];
  return { type: "and", filters: filterList };
}

/**
 * Count active filters in a FiltersState
 */
function countActiveFilters(filters: FiltersState): number {
  let count = 0;
  if (filters.clientIds.length > 0) count++;
  if (filters.apsCategory.length > 0) count++;
  if (filters.apsSubcategory.length > 0) count++;
  if (filters.audience !== null) count++;
  if (filters.externalReview !== null) count++;
  if (filters.promotionType !== null) count++;
  if (filters.nocStatus !== null) count++;
  if (filters.topic.length > 0) count++;
  if (filters.startYear !== null) count++;
  if (filters.approvalYear !== null) count++;
  return count;
}

/**
 * Check if a FiltersState has any active filters
 */
function hasAnyActiveFilters(filters: FiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

// ========== Hook ==========

/**
 * Metadata filters hook
 * @param excludedClientIds - Client IDs that the user CANNOT access (always excluded from results)
 */
export const useMetadataFilters = (excludedClientIds: string[] = []) => {
  const [state, dispatch] = useReducer(
    filterReducer,
    undefined,
    getInitialState
  );

  // Persist state to localStorage when it changes
  useEffect(() => {
    saveFiltersToStorage(state);
  }, [state]);

  // Memoized values based on pending filters (for UI display)
  const pendingFilterCount = useMemo(
    () => countActiveFilters(state.pending),
    [state.pending]
  );

  // Memoized values based on applied filters (for actual filtering)
  const hasActiveFilters = useMemo(
    () => hasAnyActiveFilters(state.applied),
    [state.applied]
  );

  const activeFilterCount = useMemo(
    () => countActiveFilters(state.applied),
    [state.applied]
  );

  // Check if pending differs from applied
  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(state.pending) !== JSON.stringify(state.applied);
  }, [state.pending, state.applied]);

  // Build file search filters from APPLIED state
  // Always generate filters when there are excluded clients (ethical wall enforcement)
  const fileSearchFilters: MetadataFilter = useMemo(() => {
    // If there are no active user filters AND no excluded clients, return null
    if (!hasActiveFilters && excludedClientIds.length === 0) return null;
    return buildFileSearchFilters(state.applied, excludedClientIds);
  }, [state.applied, hasActiveFilters, excludedClientIds]);

  // Build filters from PENDING state for filter count preview
  const pendingFileSearchFilters: MetadataFilter = useMemo(() => {
    const hasPendingFilters = hasAnyActiveFilters(state.pending);
    if (!hasPendingFilters && excludedClientIds.length === 0) return null;
    return buildFileSearchFilters(state.pending, excludedClientIds);
  }, [state.pending, excludedClientIds]);

  // Apply function - copies pending to applied
  const applyFilters = useCallback(() => {
    dispatch({ type: "apply" });
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return useMemo(
    () => ({
      // Pending filters for UI display
      pendingFilters: state.pending,
      // Applied filters for reference
      appliedFilters: state.applied,
      // Dispatch for updating pending filters
      dispatch,
      // Apply pending filters
      applyFilters,
      // Clear all filters
      clearFilters,
      // File search filters (built from applied + exclusions)
      fileSearchFilters,
      // Pending file search filters (for preview)
      pendingFileSearchFilters,
      // Excluded client IDs (clients user cannot access)
      excludedClientIds,
      // Status flags
      hasActiveFilters,
      activeFilterCount,
      pendingFilterCount,
      hasPendingChanges,
      // File count preview (removed - no Hasura query in home app)
      filesMatchingFilter: null,
      filesMatchingFilterLoading: false,
    }),
    [
      state.pending,
      state.applied,
      fileSearchFilters,
      pendingFileSearchFilters,
      excludedClientIds,
      hasActiveFilters,
      activeFilterCount,
      pendingFilterCount,
      hasPendingChanges,
      applyFilters,
      clearFilters,
    ]
  );
};

export type UseMetadataFiltersReturn = ReturnType<typeof useMetadataFilters>;
